// ---------------------------------------------------------------------------
// ContactInfo Renderer (editor key: ContactInfo)
// ---------------------------------------------------------------------------
// Renders a contact card with optional address, phone, email, and website.
// Each field is independently optional; we render only what the operator
// filled in. Phone/email get `tel:` / `mailto:` links via the URL safelist,
// so unsupported schemes (`javascript:`, etc.) collapse to `#`. Address is
// emitted inside `<address>` with an `itemtype` hook for downstream
// structured-data enrichment — without inlining JSON-LD here (that lives in
// the SEO pass).

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface ContactInfoProps {
  heading?: string;
  organization?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
}

function buildTelHref(value: string): string {
  // tel: links accept digits, +, -, spaces, parens. Strip everything else
  // and add the scheme so the URL safelist accepts it.
  const cleaned = value.replace(/[^0-9+\-() ]/g, "").trim();
  if (!cleaned) return "#";
  return `tel:${cleaned.replace(/\s+/g, "")}`;
}

function buildMailtoHref(value: string): string {
  // Minimal local check; the URL safelist handles the rest.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "#";
  return `mailto:${value}`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as ContactInfoProps;

  const heading = typeof p.heading === "string" ? p.heading.trim() : "";
  const organization =
    typeof p.organization === "string" ? p.organization.trim() : "";
  const address = typeof p.address === "string" ? p.address.trim() : "";
  const phone = typeof p.phone === "string" ? p.phone.trim() : "";
  const email = typeof p.email === "string" ? p.email.trim() : "";
  const website = typeof p.website === "string" ? p.website.trim() : "";

  if (!organization && !address && !phone && !email && !website) {
    return "<!-- contact-info missing fields -->";
  }

  const headingHtml = heading
    ? `\n  <h2 class="ease-contact-info__heading">${text(heading)}</h2>`
    : "";
  const orgHtml = organization
    ? `\n    <p class="ease-contact-info__org">${text(organization)}</p>`
    : "";
  const addressHtml = address
    ? `\n    <address class="ease-contact-info__address" itemtype="https://schema.org/PostalAddress">${text(address)}</address>`
    : "";
  const phoneHtml = phone
    ? `\n    <a class="ease-contact-info__phone" href="${url(buildTelHref(phone))}">${text(phone)}</a>`
    : "";
  const emailHtml = email
    ? `\n    <a class="ease-contact-info__email" href="${url(buildMailtoHref(email))}">${text(email)}</a>`
    : "";
  const websiteHtml = website
    ? `\n    <a class="ease-contact-info__website" href="${url(website)}">${text(website)}</a>`
    : "";

  return `<section class="ease-section ease-contact-info" itemscope itemtype="https://schema.org/Organization">${headingHtml}
  <div class="ease-contact-info__body">${orgHtml}${addressHtml}${phoneHtml}${emailHtml}${websiteHtml}
  </div>
</section>`;
}

registerRenderer("contact-info", { render });
